import { supabase } from './supabase';
import type { 
  RecurringActionLine, 
  FrequencyConfig,
  EndCondition,
  ApiResponse 
} from '../types/database';

export interface RecurringActionLineInsert {
  company_id: string;
  action_date: string;
  description: string;
  amount: number;
  bank_account_id: string;
  accounting_category_id: string;
  offset_code_id?: string | null;
  /** Set to true to prevent automatic rollover for this line. */
  disable_rollover?: boolean;
  /** Snooze until date (YYYY-MM-DD). NULL clears the snooze. */
  snoozed_until?: string | null;
}

/**
 * Service for managing recurring action lines
 */
class RecurringActionsService {
  /**
   * Get all recurring action lines for a company
   */
  async getRecurringActions(companyId: string): Promise<ApiResponse<RecurringActionLine[]>> {
    try {
      // Time-anchored window: start from the 1st of the previous month so that
      // unmatched items from last month are always visible, and far-future
      // projections (e.g. 2032) don't consume the row budget.
      const now = new Date();
      const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        .toISOString()
        .split('T')[0]!;

      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .select(`
          *,
          bank_accounts (
            nickname
          ),
          accounting_categories (
            name,
            cashflow_mapping
          ),
          offset_codes (
            name
          )
        `)
        .eq('company_id', companyId)
        .gte('action_date', startOfPreviousMonth)
        .order('action_date', { ascending: true })
        .limit(2000);

      if (error) throw error;

      return {
        data: data as RecurringActionLine[],
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Get recurring actions error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to fetch recurring actions',
        },
        status: 500,
      };
    }
  }

  /**
   * Bulk insert recurring action lines
   */
  async bulkInsertLines(
    lines: RecurringActionLineInsert[]
  ): Promise<ApiResponse<RecurringActionLine[]>> {
    try {
      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .insert(lines)
        .select();

      if (error) throw error;

      return {
        data: data as RecurringActionLine[],
        error: null,
        status: 201,
      };
    } catch (error) {
      console.error('Bulk insert recurring lines error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to insert recurring action lines',
        },
        status: 500,
      };
    }
  }

  /**
   * Date calculation engine - generates action dates based on frequency and end condition
   */
  generateActionDates(
    startDate: Date,
    frequencyConfig: FrequencyConfig,
    endCondition: EndCondition
  ): Date[] {
    const dates: Date[] = [];
    let currentDate = new Date(startDate);
    let occurrenceCount = 0;
    const maxOccurrences = endCondition.type === 'after_occurrences' ? endCondition.occurrences || 1 : 1000;
    const endDate = endCondition.type === 'specific_date' && endCondition.specificDate 
      ? new Date(endCondition.specificDate) 
      : null;

    while (occurrenceCount < maxOccurrences) {
      // Check if we've reached the end date
      if (endDate && currentDate > endDate) {
        break;
      }

      // Add current date to the list
      dates.push(new Date(currentDate));
      occurrenceCount++;

      // Calculate next date based on frequency type
      switch (frequencyConfig.type) {
        case 'daily':
          currentDate.setDate(currentDate.getDate() + 1);
          break;

        case 'weekly_5': // 5 business days (skip weekends)
          do {
            currentDate.setDate(currentDate.getDate() + 1);
          } while (currentDate.getDay() === 0 || currentDate.getDay() === 6);
          break;

        case 'weekly_6': // 6 days (skip one day)
          currentDate.setDate(currentDate.getDate() + 1);
          if (currentDate.getDay() === 0) { // Skip Sunday
            currentDate.setDate(currentDate.getDate() + 1);
          }
          break;

        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;

        case 'monthly':
          // For monthly with specific days, we need to handle differently
          if (frequencyConfig.daysOfMonth && frequencyConfig.daysOfMonth.length > 0) {
            const currentDay = currentDate.getDate();
            const sortedDays = [...frequencyConfig.daysOfMonth].sort((a, b) => a - b);
            
            // Find next day in the current month or move to next month
            let nextDay = sortedDays.find(day => day > currentDay);
            
            if (nextDay) {
              currentDate.setDate(nextDay);
            } else {
              // Move to first day of next month
              currentDate.setMonth(currentDate.getMonth() + 1);
              const firstDay = sortedDays[0];
              if (firstDay) {
                currentDate.setDate(firstDay);
              }
            }
          } else {
            // Default: add one month
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
          break;

        case 'quarterly':
          currentDate.setMonth(currentDate.getMonth() + 3);
          break;

        case 'semi_annually':
          currentDate.setMonth(currentDate.getMonth() + 6);
          break;

        case 'annually':
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          break;

        case 'custom':
          // For custom dates, we'll handle them separately outside this loop
          break;
      }
    }

    // Handle custom dates separately
    if (frequencyConfig.type === 'custom' && frequencyConfig.customDates) {
      return frequencyConfig.customDates
        .filter(dateStr => dateStr && dateStr.trim() !== '')
        .map(dateStr => new Date(dateStr))
        .filter(date => !isNaN(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    }

    // Handle quarterly/semi-annually/annually with specific dates
    if (frequencyConfig.type === 'quarterly' && frequencyConfig.quarterlyDates) {
      return frequencyConfig.quarterlyDates
        .filter(dateStr => dateStr && dateStr.trim() !== '')
        .map(dateStr => new Date(dateStr))
        .filter(date => !isNaN(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    }

    if (frequencyConfig.type === 'semi_annually' && frequencyConfig.semiAnnuallyDates) {
      return frequencyConfig.semiAnnuallyDates
        .filter(dateStr => dateStr && dateStr.trim() !== '')
        .map(dateStr => new Date(dateStr))
        .filter(date => !isNaN(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime());
    }

    if (frequencyConfig.type === 'annually' && frequencyConfig.annuallyDate) {
      return [new Date(frequencyConfig.annuallyDate)];
    }

    return dates;
  }

  /**
   * Update a single recurring action line
   */
  async updateLine(lineId: string, updates: Partial<RecurringActionLineInsert>): Promise<ApiResponse<RecurringActionLine>> {
    try {
      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .update(updates)
        .eq('id', lineId)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as RecurringActionLine,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Update recurring line error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to update recurring action line',
        },
        status: 500,
      };
    }
  }

  /**
   * Delete a single recurring action line
   */
  async deleteLine(lineId: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('recurring_actions_lines')
        .delete()
        .eq('id', lineId);

      if (error) throw error;

      return {
        data: null,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Delete recurring line error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to delete recurring action line',
        },
        status: 500,
      };
    }
  }

  /**
   * Bulk delete recurring action lines
   */
  async bulkDeleteLines(lineIds: string[]): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('recurring_actions_lines')
        .delete()
        .in('id', lineIds);

      if (error) throw error;

      return {
        data: null,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Bulk delete recurring lines error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to delete recurring action lines',
        },
        status: 500,
      };
    }
  }

  /**
   * Toggle line status between 'pending' and 'ignored'
   */
  async toggleLineStatus(lineId: string, currentStatus: string): Promise<ApiResponse<RecurringActionLine>> {
    try {
      // Don't allow toggling if it's matched
      if (currentStatus === 'matched') {
        return {
          data: null,
          error: {
            message: 'לא ניתן לשנות סטטוס של פעולה מותאמת',
          },
          status: 400,
        };
      }

      const newStatus = currentStatus === 'ignored' ? 'pending' : 'ignored';

      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .update({ status: newStatus })
        .eq('id', lineId)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as RecurringActionLine,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Toggle line status error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to toggle line status',
        },
        status: 500,
      };
    }
  }
  /**
   * Snooze a recurring action line until `untilDate` (YYYY-MM-DD).
   * Sets snoozed_until and keeps disable_rollover = false.
   * The effective date becomes GREATEST(action_date, untilDate, TODAY).
   */
  async snoozeUntil(lineId: string, untilDate: string): Promise<ApiResponse<RecurringActionLine>> {
    try {
      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .update({ snoozed_until: untilDate, disable_rollover: false })
        .eq('id', lineId)
        .select()
        .single();

      if (error) throw error;

      return { data: data as RecurringActionLine, error: null, status: 200 };
    } catch (error) {
      console.error('Snooze recurring line error:', error);
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : 'Failed to snooze line' },
        status: 500,
      };
    }
  }

  /**
   * Clear the snooze on a recurring action line (snoozed_until → NULL).
   * The line resumes normal rolling (effective date = TODAY).
   */
  async cancelSnooze(lineId: string): Promise<ApiResponse<RecurringActionLine>> {
    try {
      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .update({ snoozed_until: null })
        .eq('id', lineId)
        .select()
        .single();

      if (error) throw error;

      return { data: data as RecurringActionLine, error: null, status: 200 };
    } catch (error) {
      console.error('Cancel snooze recurring line error:', error);
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : 'Failed to cancel snooze' },
        status: 500,
      };
    }
  }

  /**
   * Bulk-set disable_rollover = true for multiple lines in a single UPDATE.
   * Used by the "ביטול גלגול" (Cancel Rollover) bulk action.
   */
  async bulkCancelRollover(lineIds: string[]): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('recurring_actions_lines')
        .update({ disable_rollover: true })
        .in('id', lineIds);

      if (error) throw error;

      return { data: null, error: null, status: 200 };
    } catch (error) {
      console.error('Bulk cancel rollover error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to cancel rollover for selected lines',
        },
        status: 500,
      };
    }
  }

  /**
   * Bulk-edit multiple recurring action lines.
   *
   * `sharedUpdates`  — fields that are identical for every selected row
   *                    (description, amount, accounting_category_id).
   * `perRowDates`    — when the user chose a "day of month", each row gets
   *                    its own action_date (original year+month, new day).
   *                    Empty array → no date change.
   *
   * Uses Promise.all so all rows are updated in parallel.
   */
  async bulkEditLines(
    perRowUpdates: Array<{ id: string; updates: Partial<RecurringActionLineInsert> }>,
  ): Promise<ApiResponse<void>> {
    try {
      await Promise.all(
        perRowUpdates.map(({ id, updates }) =>
          supabase
            .from('recurring_actions_lines')
            .update(updates)
            .eq('id', id)
            .then(({ error }) => { if (error) throw error; }),
        ),
      );

      return { data: null, error: null, status: 200 };
    } catch (error) {
      console.error('Bulk edit lines error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to bulk-edit recurring action lines',
        },
        status: 500,
      };
    }
  }

  /**
   * Toggle the disable_rollover flag for a recurring action line.
   * Returns the updated record.
   */
  async toggleRollover(lineId: string, currentDisabled: boolean): Promise<ApiResponse<RecurringActionLine>> {
    try {
      const { data, error } = await supabase
        .from('recurring_actions_lines')
        .update({ disable_rollover: !currentDisabled })
        .eq('id', lineId)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data as RecurringActionLine,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Toggle recurring rollover error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to toggle rollover',
        },
        status: 500,
      };
    }
  }
}

export const recurringActionsService = new RecurringActionsService();
